import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useGetSetting from "@insite/client-framework/Common/Hooks/useGetSetting";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Page from "@insite/mobius/Page";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import Button, { ButtonPresentationProps } from "@rc/MobiusOverrides/Button/Button";
import { screen } from "@rc/XDL/Helpers";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

export interface CookiePrivacyPolicyPopupStyles {
    drawer?: DrawerPresentationProps;
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    buttonsGridItem?: GridItemProps;
    acceptButton?: ButtonPresentationProps;
    closeButton?: ButtonPresentationProps;
}

export const cookiePrivacyPolicyPopupStyles: CookiePrivacyPolicyPopupStyles = {
    drawer: {
        position: "bottom",
        size: 220,
        isAutoSize: true,
        cssOverrides: {
            drawerContainer: css`
                transform: none !important;
            `,
            drawerTitle: css`
                display: none;
            `,
            drawerContent: css`
                padding: 25px 0;
                margin: 0 auto;
                width: 100%;
            `,
        },
        closeButtonProps: {
            css: css`
                display: none;
            `,
        },
        enableClickThrough: true,
    },
    container: {
        gap: 10,
        css: css`
            margin: 0 auto;
        `,
    },
    messageGridItem: {
        width: 12,
    },
    buttonsGridItem: {
        width: 12,
        css: css`
            padding: 30px 0 10px 0;
            display: flex;
            margin-top: 0;
            justify-content: flex-start;

            button {
                margin-right: 20px;
                &, span {
                    font-size: 14px;
                    font-weight: 500;
                    letter-spacing: 0;
                    line-height: 18px;
                }
            }

            button + button {
                margin-right: 0;
                margin-left: 0;
            }

            ${screen("lte", "xs")`
                justify-content: center;
                text-align: center;
                flex-wrap: wrap;
                padding-left: 0;
                padding-right: 0;
                margin-left: 0;
                margin-right: 0;

                button {
                    margin-right: 0;
                    margin-left: 0;
                    margin-bottom: 10px;
                    width: 100%;
                }
            `}
        }`,
    },

    closeButton: {
        variant: "secondary",
    },
    messageText: {
        size: 14,
    },
};

const styles = cookiePrivacyPolicyPopupStyles;

const CookiePrivacyPolicyPopup = () => {
    const location = useAppSelector(o => getLocation(o));
    const enableCookiePrivacyPolicyPopup = useGetSetting(o => o.websiteSettings.enableCookiePrivacyPolicyPopup);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (enableCookiePrivacyPolicyPopup && !getCookie("acceptCookies")) {
            setIsOpen(true);
        }
    }, [location.pathname]);

    const acceptHandler = () => {
        setIsOpen(false);
        const today = new Date();
        const nxt180Days = new Date(new Date().setDate(today.getDate() + 180));
        setCookie("acceptCookies", "true", { expires: nxt180Days });
    };

    const closeHandler = () => {
        setIsOpen(false);
    };

    return (
        <Drawer isOpen={isOpen} {...styles.drawer} data-test-selector="drawer_CookiePolicy">
            <Page>
                <GridContainer {...styles.container}>
                    <GridItem {...styles.messageGridItem}>
                        <Typography {...styles.messageText} data-test-selector="cookiePolicy_PrivacyPolicy">
                            {siteMessage("Core_CookiePrivacyPolicy")}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.buttonsGridItem} className="modal-actions">
                        <Button
                            {...styles.acceptButton}
                            onClick={acceptHandler}
                            data-test-selector="cookiePolicy_AcceptCookies"
                        >
                            {siteMessage("Core_CookiePrivacyPolicyAccept")}
                        </Button>
                        <Button
                            {...styles.closeButton}
                            onClick={closeHandler}
                            aria-label={translate("Close")}
                            data-test-selector="cookiePolicy_ClosePolicy"
                        >
                            {translate("Decline")}
                        </Button>
                    </GridItem>
                </GridContainer>
            </Page>
        </Drawer>
    );
};

export default CookiePrivacyPolicyPopup;
